/**
 * Single source of truth for the org's areas. Seeded from the legacy GAS
 * dropdown at `GAS (legacy)/index.html:1517-1545` and extended since — `IT` and
 * `CORPORATE SYSTEMS` are additions, NOT drift to reconcile against GAS. Used by:
 *
 * - The onboarding dialog at first sign-in (forces a pick from this list).
 * - The Profile page area selector.
 * - The ResourceForm / BulkShareDialog area pickers.
 * - The backend zod write schemas (`createUserSchema`, `updateSelfSchema`).
 *
 * Reads of legacy user docs still accept arbitrary strings (per the
 * "Accept legacy + new restricted" policy agreed with the user) — this list
 * only constrains *new* writes.
 */
export const AREAS = [
  'CEO',
  'PRESIDENT',
  'MEDIA PRESIDENT',
  'CHIEF OF STAFF',
  'CFO',
  'ADMIN, ACCOUNTING & FINANCE',
  'CORPORATE DEVELOPMENT',
  'CBO',
  'LEGAL',
  'CMO',
  'HUMAN RESOURCES',
  'ON FIELD OPERATIONS',
  'MAINTENANCE',
  'REMOTE & MEDIA OPERATIONS',
  'TRUCKADS & PRINTING',
  'AUTONOMY',
  'HARDWARE & MANUFACTURING',
  'DESIGN & PRODUCT INNOVATION',
  'ADVANCED ROBOTICS',
  'DIGITAL PRODUCT',
  'SERVICE DESK',
  'IT',
  'CORPORATE SYSTEMS',
  'DATA',
  'PROCESS INTELLIGENCE',
  'LOGISTICS',
  'PRODUCT',
] as const;

export type Area = (typeof AREAS)[number];

/**
 * The CEO area is restricted to a hardcoded email allowlist. The actual list
 * lives server-side in the `CEO_EMAILS` env var (see `apps/api/src/env.ts`).
 * The backend tells the client whether the current user is allowed to pick
 * this area via `MeResponse.canPickCeoArea`.
 */
export const CEO_AREA: Area = 'CEO';

/** Type guard — does NOT validate legacy values. */
export function isOfficialArea(value: unknown): value is Area {
  return typeof value === 'string' && (AREAS as readonly string[]).includes(value);
}
